import { AlertTriangle } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";

import type { PipelineSurfaceStatus } from "@busright/data-contract";

import { DsDupSerials } from "@/components/DsDupSerials";
import { DsErdCanvas } from "@/components/DsErdCanvas";
import { DsPipelineHealth } from "@/components/DsPipelineHealth";
import { type DsFocusRequest, DsRawExplorer } from "@/components/DsRawExplorer";
import { EmptyState } from "@/components/EmptyState";
import { InsightCallout, type InsightStat } from "@/components/InsightCallout";
import { PageHeader } from "@/components/PageHeader";
import { useDupSerials, usePipelineHealth } from "@/hooks/queries";
import { useExploreTables } from "@/hooks/useExploreTables";
import { formatInt } from "@/lib/format";

// Count of KNOWN-MISSING joins drawn as dashed-red edges in the ERD (DsErdCanvas.BROKEN_EDGES). The
// headline order↔serial gap is the most consequential of these — it's why device↔order lineage is
// guesswork today. Kept in sync with the ERD by hand (the ERD owns the authoritative list).
const KNOWN_MISSING_JOINS = 3;

interface DsInsight {
  readonly headline: string;
  readonly stats: readonly InsightStat[];
}

// Compute the "so what" for data sources: how many curated surfaces are stale/broken, the standing
// fact that the order↔serial join is missing, and the count of duplicate/blank Esper serials that
// corrupt serial-keyed joins. This is a DATA-QUALITY readout, NOT a fleet health verdict — severity
// stays NEUTRAL (never RAG).
const buildDsInsight = (
  surfaces: readonly PipelineSurfaceStatus[],
  duplicateSerialCount: number,
  blankSerialCount: number,
): DsInsight | null => {
  if (surfaces.length === 0) {
    return null;
  }
  const staleOrBroken = surfaces.filter((s) => s.staleness !== "FRESH").length;
  const dupIssues = duplicateSerialCount + blankSerialCount;
  const headline = `${formatInt(staleOrBroken)} of ${formatInt(surfaces.length)} source${surfaces.length === 1 ? "" : "s"} are stale or broken, the order ↔ device-serial join is still missing, and ${formatInt(dupIssues)} Esper serials (${formatInt(duplicateSerialCount)} duplicate, ${formatInt(blankSerialCount)} blank) need cleanup — device-to-order lineage is guesswork today and the universal join key isn't fully clean.`;
  const stats: InsightStat[] = [
    { label: "stale / broken", value: formatInt(staleOrBroken) },
    { label: "sources tracked", value: formatInt(surfaces.length) },
    { label: "known-missing joins", value: formatInt(KNOWN_MISSING_JOINS) },
    { label: "dup / blank serials", value: formatInt(dupIssues) },
  ];
  return { headline, stats };
};

// Data Sources & Health — the master utility view. Four stacked sections, each crystal clear about
// what a source is and whether it can be trusted:
//   1. Pipeline health — every curated surface, freshness + cadence + row count, sortable.
//   2. ERD canvas — the live join graph (all warehouse objects), with real/inferred/broken edges.
//   3. Duplicate & blank serials — the R12 data-quality worklist for Esper serial cleanup.
//   4. Raw explorer — read-only, allow-listed staging-table preview with client-side CSV export.
// Clicking a pipeline row or an ERD node focuses the raw explorer on that table (when explorable).
export const DataSourcesPage = (): ReactNode => {
  const health = usePipelineHealth();
  const tables = useExploreTables();
  const dupSerials = useDupSerials();

  const meta = health.data?.meta;
  const payload = health.data?.data;
  const surfaces = payload?.surfaces ?? [];

  const dupPayload = dupSerials.data?.data;
  const dupRows = dupPayload?.rows ?? [];
  const duplicateSerialCount = dupPayload?.duplicateSerialCount ?? 0;
  const duplicateRowCount = dupPayload?.duplicateRowCount ?? 0;
  const blankSerialCount = dupPayload?.blankSerialCount ?? 0;

  const insight = useMemo(
    () => buildDsInsight(surfaces, duplicateSerialCount, blankSerialCount),
    [surfaces, duplicateSerialCount, blankSerialCount],
  );

  // The raw explorer's focus request. A fresh object (new nonce) on each click so re-clicking the
  // same table re-applies the focus and re-scrolls even though the table name is unchanged.
  const [focusRequest, setFocusRequest] = useState<DsFocusRequest | null>(null);
  const handleFocusTable = useCallback((table: string): void => {
    setFocusRequest((prev) => ({ table, nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);

  return (
    <>
      <PageHeader
        title="Data Sources & Health"
        description="Every source table, its freshness, how the datasets join (and where they can't), duplicate/blank serials to clean up, and a read-only staging-table explorer."
        meta={meta}
      />

      {insight ? (
        <InsightCallout
          headline={insight.headline}
          stats={insight.stats}
          severity="neutral"
          icon={AlertTriangle}
        />
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {health.isError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Pipeline health unavailable"
            message={
              health.error instanceof Error
                ? health.error.message
                : "Could not load pipeline health from the API."
            }
          />
        ) : (
          <DsPipelineHealth
            surfaces={surfaces}
            accountMatchRatePct={payload?.accountMatchRatePct ?? 0}
            loading={health.isLoading}
            onFocusTable={handleFocusTable}
          />
        )}

        <DsErdCanvas
          surfaces={surfaces}
          loading={health.isLoading}
          onFocusTable={handleFocusTable}
        />

        {dupSerials.isError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Duplicate-serial scan unavailable"
            message={
              dupSerials.error instanceof Error
                ? dupSerials.error.message
                : "Could not load the duplicate-serial data-quality scan from the API."
            }
          />
        ) : (
          <DsDupSerials
            rows={dupRows}
            duplicateSerialCount={duplicateSerialCount}
            duplicateRowCount={duplicateRowCount}
            blankSerialCount={blankSerialCount}
            loading={dupSerials.isLoading}
          />
        )}

        <DsRawExplorer
          tables={tables.data?.tables ?? []}
          tablesLoading={tables.isLoading}
          tablesError={tables.isError}
          focusRequest={focusRequest}
        />
      </div>
    </>
  );
};
